import { Publish, Visibility, VisibilityOff } from '@mui/icons-material';
import { IconButton, Tooltip } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { type FC, useState } from 'react';

import { useDraftTimelogStore } from '@/features/draftTimelogs/model';
import { LogsMode } from '@/features/logsMode';
import { pluralize } from '@/utils/pluralize';

import { PublishDraftsConfirmDialog } from '../PublishDraftsConfirmDialog';
import { PublishTargetMenu } from '../PublishTargetMenu';

import s from './DraftIndicator.module.scss';

const DRAFT_COUNT_VARIANTS = {
  one: 'неопубликованный черновик',
  two: 'неопубликованных черновика',
  five: 'неопубликованных черновиков',
} as const;

export const DraftIndicator: FC = observer(() => {
  const {
    isViewingSelf,
    hasDrafts,
    draftFilter,
    setDraftFilter,
    unpublishedDraftCount,
    publishBulk,
    viewedDraftPeriodIso,
    draftsWithoutTaskCount,
    loadingStage,
  } = useDraftTimelogStore();

  // Куда публиковать, пока открыто подтверждение про черновики без задачи
  const [confirmTarget, setConfirmTarget] = useState<LogsMode | null>(null);

  if (!isViewingSelf || !hasDrafts || loadingStage.isLoading) {
    return null;
  }

  const countTooltip = `${pluralize(unpublishedDraftCount, DRAFT_COUNT_VARIANTS)} за просматриваемый период`;

  const isDraftsOnly = draftFilter === 'drafts';

  const handleFilterClick = () => {
    setDraftFilter(isDraftsOnly ? 'all' : 'drafts');
  };

  const runPublish = async (target: LogsMode) => {
    const { dateFrom, dateTo } = viewedDraftPeriodIso;

    await publishBulk(dateFrom, dateTo, target);
  };

  const handlePublish = (target: LogsMode) => {
    if (draftsWithoutTaskCount > 0) {
      setConfirmTarget(target);
    } else {
      void runPublish(target);
    }
  };

  return (
    <>
      <div className={s.root}>
        <Tooltip title={countTooltip} arrow>
          <span className={s.count}>{unpublishedDraftCount}</span>
        </Tooltip>
        <Tooltip title={isDraftsOnly ? 'Показать все записи' : 'Показать только черновики'} arrow>
          <IconButton
            size="small"
            onClick={handleFilterClick}
            aria-label={isDraftsOnly ? 'Показать все записи' : 'Показать только черновики'}
            sx={{
              padding: '4px',
              color: isDraftsOnly ? 'var(--accent-color)' : 'var(--secondary-color)',
            }}
          >
            {isDraftsOnly ? (
              <VisibilityOff sx={{ fontSize: 20 }} />
            ) : (
              <Visibility sx={{ fontSize: 20 }} />
            )}
          </IconButton>
        </Tooltip>
        <PublishTargetMenu onPublish={handlePublish}>
          {(open) => (
            <Tooltip title="Опубликовать все черновики за период" arrow>
              <IconButton
                size="small"
                onClick={open}
                aria-label="Опубликовать все черновики"
                sx={{ padding: '4px', color: 'var(--accent-color)' }}
              >
                <Publish sx={{ fontSize: 20 }} />
              </IconButton>
            </Tooltip>
          )}
        </PublishTargetMenu>
      </div>
      <PublishDraftsConfirmDialog
        open={confirmTarget !== null}
        onClose={() => setConfirmTarget(null)}
        draftsWithoutTaskCount={draftsWithoutTaskCount}
        onConfirmPublish={() => (confirmTarget ? runPublish(confirmTarget) : undefined)}
      />
    </>
  );
});
